import { Button, Drawer, Flex, Tag, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../utils/cn.ts"
import {
  customerEngagementLabel,
  describeCreatedBy,
  editionLabel,
  formatEditionDate,
  salesPersonEngagementLabel
} from "./education-track-helpers.ts"
import type { EditionView } from "./types/education.types.ts"

const { Text, Title } = Typography

const formatDurationMs = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, "0")}`
}

interface EditionTimelineProps {
  open: boolean
  onClose: () => void
  editions: EditionView[]
  maxEditions: number
  activeEditionId: string
  /** The signed-in account's own date format — see `EditionHeader`'s prop
   *  of the same name for why this stays a plain string, not a hook call. */
  dateFormat: string
  onOpenEdition: (editionId: string) => void
  onPlayEdition: (editionId: string) => void
  onCopyEdition: (editionId: string) => void
  onReportIssue: (editionId: string) => void
}

/**
 * Screen 4 — opened from the "History" link. Nodes are ordered Base,
 * Section 2, Section 3; selecting one shows its own meta and actions without
 * leaving the drawer, since deciding which edition to open/play/report
 * doesn't require committing to it first.
 */
export function EditionTimeline({
  open,
  onClose,
  editions,
  maxEditions,
  activeEditionId,
  dateFormat,
  onOpenEdition,
  onPlayEdition,
  onCopyEdition,
  onReportIssue
}: EditionTimelineProps) {
  const { t } = useTranslation()
  const [focusedId, setFocusedId] = useState(activeEditionId)

  useEffect(() => {
    if (open) setFocusedId(activeEditionId)
  }, [open, activeEditionId])

  const focused = editions.find((edition) => edition.id === focusedId) ?? editions[0]

  const focusText = (edition: EditionView) =>
    (edition.focus_codes?.length ? edition.focus_codes : edition.focus_code.split(","))
      .map((code) => code.trim())
      .filter(Boolean)
      .map((code) => t(`projectEducation.askMore.focus.${code}`, { defaultValue: code }))
      .join(", ")
  const exhausted = editions.length >= maxEditions

  return (
    <Drawer
      title={t("projectEducation.editionTimeline.title")}
      open={open}
      onClose={onClose}
      width={480}
      destroyOnHidden
    >
      <Flex wrap gap={"middle"} data-testid={"edition-timeline-nodes"}>
        {editions.map((edition) => (
          <button
            key={edition.id}
            type={"button"}
            data-testid={`timeline-node-${edition.id}`}
            className={cn(
              "flex flex-col items-start rounded border px-3 py-2 text-left",
              edition.id === focusedId ? "border-primary bg-primary-bg" : "border-border"
            )}
            onClick={() => setFocusedId(edition.id)}
          >
            <Text strong>{editionLabel(edition, t)}</Text>
            <Text type={"secondary"} className={"text-xs"}>
              {formatEditionDate(edition.created_at, dateFormat)}
            </Text>
            <Flex gap={4} wrap className={"mt-1"}>
              <Tag
                data-testid={`engagement-sales-${edition.id}`}
                className={"text-xs"}
                bordered={false}
              >
                {t("projectEducation.editionTimeline.engagement.salesLabel")}:{" "}
                {salesPersonEngagementLabel(edition.engagement.sales_person, dateFormat, t)}
              </Tag>
              {edition.engagement.customer && (
                <Tag
                  data-testid={`engagement-customer-${edition.id}`}
                  className={"text-xs"}
                  color={"blue"}
                  bordered={false}
                >
                  {t("projectEducation.editionTimeline.engagement.customerLabel")}:{" "}
                  {customerEngagementLabel(edition.engagement.customer, dateFormat, t)}
                </Tag>
              )}
            </Flex>
          </button>
        ))}
      </Flex>

      {focused && (
        <div className={"mt-4"} data-testid={"edition-timeline-meta"}>
          <Title level={5}>{editionLabel(focused, t)}</Title>
          <Flex vertical gap={4} className={"text-sm"}>
            <Text>
              {t("projectEducation.editionTimeline.focus", {
                focus: focusText(focused) || t("projectEducation.editionTimeline.focusNone")
              })}
            </Text>
            <Text>{describeCreatedBy(focused.created_by, t)}</Text>
            <Text>
              {t("projectEducation.editionTimeline.promptVersion", {
                version: focused.prompt_version
              })}
            </Text>
            <Text>
              {focused.audio?.status === "ready" && focused.audio.duration_ms != null
                ? t("projectEducation.editionTimeline.audioReady", {
                    time: formatDurationMs(focused.audio.duration_ms)
                  })
                : t("projectEducation.editionTimeline.audioNotPrepared")}
            </Text>
            <Text>
              {t("projectEducation.editionTimeline.sources", {
                count: focused.citations.length
              })}
            </Text>
            {focused.safety_flags.length === 0 ? (
              <Text>{t("projectEducation.editionTimeline.qualityPassed")}</Text>
            ) : (
              <ul className={"list-disc pl-5"}>
                {focused.safety_flags.map((flag, index) => (
                  <li key={`${flag.code}-${index}`}>
                    {flag.code}
                    {flag.severity ? ` (${flag.severity})` : ""}
                  </li>
                ))}
              </ul>
            )}
          </Flex>

          <Flex gap={"small"} className={"mt-4"} wrap>
            <Button onClick={() => onOpenEdition(focused.id)}>
              {t("projectEducation.editionTimeline.openEdition")}
            </Button>
            <Button onClick={() => onPlayEdition(focused.id)}>
              {t("projectEducation.editionTimeline.play")}
            </Button>
            <Button onClick={() => onCopyEdition(focused.id)}>
              {t("projectEducation.editionTimeline.copy")}
            </Button>
            <Button onClick={() => onReportIssue(focused.id)}>
              {t("projectEducation.editionTimeline.reportIssue")}
            </Button>
          </Flex>
        </div>
      )}

      {exhausted && (
        <Text type={"secondary"} className={"mt-4 block"}>
          {t("projectEducation.editionTimeline.exhausted")}
        </Text>
      )}
    </Drawer>
  )
}
